import React, { forwardRef } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Typography,
  Space,
  Statistic,
  Row,
  Col,
  Divider,
} from "antd";
import {
  ClockCircleOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  AimOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import {
  colorStatusInfraction,
  formatDateTime,
  SOURCE_TYPES,
  statusText,
} from "../../utils/constants";
import { IMAGE_URL } from "../../constants";
import { countries } from "../../utils/countries";

const { Title, Text } = Typography;

const InfractionScreenshotCard = forwardRef(({ infraction }, ref) => {
  const renderStatusInfo = () => {
    if (infraction.status === "paid" && infraction?.payment) {
      return (
        <Card size="small" className="mb-6 border-green-500">
          <div className="flex items-center">
            <CheckCircleOutlined className="text-green-500 text-xl mr-2" />
            <div className="w-full">
              <Text strong>
                {infraction.payment.source === "citizen_web" ||
                infraction.payment.source === "citizen_mobile"
                  ? `Payée via ${SOURCE_TYPES[infraction.payment.source]}`
                  : "Payée par:"}
              </Text>
              <div className="flex items-center mt-1">
                {infraction.payment.source === "citizen_web" ||
                infraction.payment.source === "citizen_mobile" ? (
                  <>
                    <Tag color="blue" className="mr-2">
                      {SOURCE_TYPES[infraction.payment.source]}
                    </Tag>
                    <Text>Paiement automatique</Text>
                  </>
                ) : (
                  <>
                    <UserOutlined className="text-green-500 mr-2" />
                    <Text>
                      {infraction.payment.user?.full_name || "Agent"}
                    </Text>
                  </>
                )}
                <Text type="secondary" className="ml-2">
                  {formatDateTime(infraction.payment.payment_date)}
                </Text>
              </div>
              {infraction.payment.reference_number && (
                <div className="flex items-center mt-1">
                  <Text strong className="mr-2">
                    Référence:
                  </Text>
                  <Text code>{infraction.payment.reference_number}</Text>
                </div>
              )}
              {infraction.payment.payment_method && (
                <div className="flex items-center mt-1">
                  <Text strong className="mr-2">
                    Méthode:
                  </Text>
                  <Tag
                    color={
                      infraction.payment.payment_method === "cmi" ||
                      infraction.payment.payment_method === "fatourati"
                        ? "blue"
                        : "green"
                    }
                  >
                    {infraction.payment.payment_method.toUpperCase()}
                  </Tag>
                </div>
              )}
            </div>
          </div>
        </Card>
      );
    }

    if (infraction.status === "canceled") {
      return (
        <Card size="small" className="mb-6 border-red-500">
          <div className="flex items-center">
            <StopOutlined className="text-red-500 text-xl mr-2" />
            <div>
              <Text strong>Annulée par:</Text>
              <div className="flex items-center mt-1">
                <UserOutlined className="text-red-500 mr-2" />
                {infraction.cancelled_by && (
                  <Text>{infraction.cancelled_by.full_name}</Text>
                )}
                <Text type="secondary" className="ml-2">
                  {formatDateTime(infraction.cancelled_at)}
                </Text>
              </div>
              {infraction.cancellation_reason && (
                <div className="mt-2">
                  <Text strong>Motif:</Text>
                  <Text className="ml-2">
                    {infraction.cancellation_reason.name}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </Card>
      );
    }

    return null;
  };

  return (
    <div
      ref={ref}
      style={{ width: "900px", padding: "32px", background: "#faf5ff" }}
    >
      <Card className="shadow-lg border-t-4 border-purple-600">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Title level={2} className="text-purple-700 m-0">
              Détails de l'Infraction #{infraction.reference}
            </Title>
            <Tag
              color={colorStatusInfraction[infraction.status]}
              className="text-lg px-4 py-1"
            >
              {statusText(infraction.status)}
            </Tag>
          </div>
        </div>

        {renderStatusInfo()}

        <Row gutter={24}>
          <Col span={14}>
            <Statistic
              title="Type d'Infraction"
              value={infraction.type_infraction?.name}
              valueStyle={{ color: "#722ed1" }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Col>
          <Col span={10}>
            <Statistic
              title="Montant de l'Amende"
              value={infraction.amount + " MAD"}
              valueStyle={{ color: "#722ed1" }}
            />
          </Col>
        </Row>

        <Divider className="my-6" />

        <Descriptions column={1} className="mb-6">
          <Descriptions.Item
            label={
              <Text strong className="text-purple-700">
                Description
              </Text>
            }
          >
            {infraction.description}
          </Descriptions.Item>
        </Descriptions>

        <Row gutter={24} className="mb-6">
          <Col span={12}>
            <Card
              size="small"
              title={
                <Text strong className="text-purple-700">
                  Informations du Véhicule
                </Text>
              }
              className="h-full"
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Matricule">
                  {infraction.matricule}
                  {infraction?.vehicle?.country &&
                    ` ( ${
                      countries.find(
                        (c) => c.code === infraction.vehicle.country
                      )?.name || ""
                    } )`}
                </Descriptions.Item>
                <Descriptions.Item label="Marque">
                  {infraction.brand}
                </Descriptions.Item>
                <Descriptions.Item label="Modèle">
                  {infraction.model}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col span={12}>
            <Card
              size="small"
              title={
                <Text strong className="text-purple-700">
                  Détails de l'Infraction
                </Text>
              }
              className="h-full"
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <ClockCircleOutlined className="mr-2" />
                      <span>Date et Heure</span>
                    </div>
                  }
                >
                  {formatDateTime(infraction.datetime_infraction)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <AimOutlined className="mr-2" />
                      <span>Contrat</span>
                    </div>
                  }
                >
                  {infraction?.zone?.name}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <AimOutlined className="mr-2" />
                      <span>Zone</span>
                    </div>
                  }
                >
                  {infraction?.section?.name || "Non défini"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <AimOutlined className="mr-2" />
                      <span>Rue</span>
                    </div>
                  }
                >
                  {infraction?.street?.name || "Non défini"}
                  {" - "}
                  {infraction?.street?.name_ar || "غير محدد"}
                </Descriptions.Item>
                <Descriptions.Item label="Coordonnées">
                  Lat: {infraction.latitude}, Long: {infraction.longitude}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>

        <Card
          size="small"
          title={
            <Text strong className="text-purple-700">
              Preuves
            </Text>
          }
          className="mb-6"
        >
          <Space direction="vertical" size="middle" className="w-full">
            {infraction.photos && (
              <div>
                <Text strong className="mr-3">
                  Photos:
                </Text>
                <Space size={[8, 8]} wrap>
                  {infraction.photos.split(",").map((photo, index) => (
                    <img
                      key={index}
                      width={150}
                      height={150}
                      src={IMAGE_URL + photo}
                      alt={`Photo d'infraction ${index + 1}`}
                      className="object-cover rounded"
                      crossOrigin="anonymous"
                    />
                  ))}
                </Space>
              </div>
            )}
            <div className="flex justify-between items-center">
              <div></div>
              <div>
                <Text strong>Agent Verbalisateur:</Text>
                <div className="flex items-center mt-2">
                  <UserOutlined className="text-purple-600 text-xl mr-2" />
                  <Text>{infraction?.user?.full_name}</Text>
                </div>
              </div>
            </div>
          </Space>
        </Card>
      </Card>
    </div>
  );
});

InfractionScreenshotCard.displayName = "InfractionScreenshotCard";

export default InfractionScreenshotCard;
